"use client"

import { useState, useEffect, useRef } from "react"
import { NavLink, useNavigate, useLocation } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import logo from "../../../assets/logo.png"
import { FiBell, FiLogOut, FiX } from "react-icons/fi"
import { MdOutlineFaceUnlock } from "react-icons/md"
import { FaCaretDown } from "react-icons/fa"
import MenuToggleIcon from "./MenuToggleIcon"
import { clearLeads, setLeadsPage } from "../../../redux/features/adminLeadSlice"
import { setPage as setApplicationLogPage } from "../../../redux/features/applicationLogSlice"
import { useSelector, useDispatch } from "react-redux"
import socketService from "../../../config/socketService"
import apiFetch from "../../../utlis/apiFetch"
import {
  addNotification,
  clearNotifications,
  setNotifications,
  updateNotification,
  // markAllNotificationsAsRead,
} from "../../../redux/features/notificationSlice"
import { logout } from "../../../redux/features/logoutSlice"
import { useCountries } from "../../client/dashboard/staticData"
import { setGlobalCountries } from "../../../redux/features/countriesSlice"
import { fetchUserInfo } from "../../../redux/features/userSlice"

const navItems = [
  { name: "Dashboard", path: "/agent" },
  { name: "Leads", path: "/agent/leads" },
  { name: "Client History", path: "/agent/client-history" },
  { name: "Application Log", path: "/agent/application-log" },
  { name: "Employees", path: "/agent/employees", permission: "employees" },
  { name: "Documents", path: "/agent/documents", permission: "documents" },
  { name: "Marketing", path: "/agent/marketing", permission: "marketing" },
]

export default function Navbar() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("user")
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const isMounted = useRef(false)
  
  // Permissions Logic
  const { permissions, userInfo } = useSelector((state) => state.user);
  const location = useLocation();

  useEffect(() => {
    dispatch(fetchUserInfo());
  }, [dispatch]);

  // Redirect if permission is revoked (defensive: only run when mounted and permissions loaded)
  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  useEffect(() => {
    if (!isMounted.current) return
    if (!userInfo) return

    // Skip redirect logic for agents (only applies to sub-admins or others)
    if (userInfo.role === 'agent') return

    const currentPath = location.pathname
    // Find the most specific nav item that matches the current path
    const matchedItem = [...navItems]
      .sort((a, b) => b.path.length - a.path.length)
      .find((item) => currentPath === item.path || currentPath.startsWith(item.path + '/'))

    // Only attempt redirect when the matched item requires a permission
    // and permissions are loaded as an array
    if (matchedItem && matchedItem.permission && Array.isArray(permissions)) {
      const hasPerm = permissions.includes(matchedItem.permission)
      if (!hasPerm) {
        // Log for debugging why a redirect would happen
        // eslint-disable-next-line no-console
        console.warn('Navbar redirect: missing permission', {
          matchedItem,
          permissions,
          userInfo,
        })
        navigate('/agent')
      }
    }
  }, [userInfo, permissions, location.pathname, navigate])

  const filteredNavItems = navItems.filter((item) => {
    if (userInfo?.role === 'agent') {
      return ["Dashboard", "Leads", "Client History", "Application Log"].includes(item.name);
    }
    if (!item.permission) return true;
    return permissions && permissions.includes(item.permission);
  });

  const countries = useCountries();
  const globalCountries = useSelector((state) => state.globalCountries.globalCountries)
  const [selectedCountries, setSelectedCountries] = useState(globalCountries || [])
  const countryDropdownRef = useRef(null)
  const hideDropdown = location.pathname.includes("employees")

  // Get notifications from Redux store
  const { notifications, isLoaded } = useSelector((state) => state.notifications)
  const [unreadCount, setUnreadCount] = useState(0)
  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const notificationRef = useRef(null)

  useEffect(() => {
    try {
      const userData = localStorage.getItem("user")
      if (!userData) {
        navigate("/")
        return
      }
      const parsed = JSON.parse(userData);      
      if (parsed?.role !== "agent" && parsed?.role !== "sub-admin") navigate("/")
      // user state already initialized synchronously; no setUser here to avoid layout shift
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error)
    }
  }, [])

  // Fetch notifications on component mount
  useEffect(() => {
    // Only fetch notifications if they haven't been loaded yet
    if (!isLoaded) {
      fetchNotifications()
    }

    // Setup socket connection
    setupSocketConnection()

    // Close notification dropdown when clicking outside
    // const handleClickOutside = (event) => {
    //   if (notificationRef.current && !notificationRef.current.contains(event.target)) {
    //     setIsNotificationOpen(false)
    //   }
    // }

    // document.addEventListener("mousedown", handleClickOutside)

    // Proper cleanup
    // return () => {
    //   document.removeEventListener("mousedown", handleClickOutside)
    // Don't disconnect the socket here, as we want to keep it alive between route changes
    // We'll only disconnect on logout
    // }
  }, [isLoaded]) // Depend on isLoaded to prevent unnecessary fetches

  // Update unread count whenever notifications change
  useEffect(() => {
    if (notifications) {
      const unreadNotifications = notifications.filter((notification) => !notification.isRead)
      setUnreadCount(unreadNotifications.length)
    }
  }, [notifications])

  useEffect(() => {
    setSelectedCountries(globalCountries)
  }, [globalCountries])

  useEffect(() => {
    if (!isOpen) return

    const handleCountryOutsideClick = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleCountryOutsideClick)
    document.addEventListener("touchstart", handleCountryOutsideClick)

    return () => {
      document.removeEventListener("mousedown", handleCountryOutsideClick)
      document.removeEventListener("touchstart", handleCountryOutsideClick)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isNotificationOpen) return

    const handleNotificationOutsideClick = (event) => {
      const clickedNotificationTrigger = event.target.closest('button[aria-label="Notifications"]')

      if (clickedNotificationTrigger) {
        return
      }

      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsNotificationOpen(false)
      }
    }

    document.addEventListener("mousedown", handleNotificationOutsideClick)
    document.addEventListener("touchstart", handleNotificationOutsideClick)

    return () => {
      document.removeEventListener("mousedown", handleNotificationOutsideClick)
      document.removeEventListener("touchstart", handleNotificationOutsideClick)
    }
  }, [isNotificationOpen])

  // Fetch notifications only if they haven't been loaded yet
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = response._data ?? await response.json()
        dispatch(setNotifications(data.body))
      }
    } catch (error) {
      console.error("Error fetching notifications:", error)
    }
  }

  const setupSocketConnection = () => {
    try {
      const userData = localStorage.getItem("user")
      if (!userData) return

      const user = JSON.parse(userData)
      const userId = user._id

      // Connect to socket server - will reuse existing connection if already connected
      socketService.connect(userId)

      // Set up notification listener
      socketService.setupNotificationListener(userId, (newNotification) => {
        console.log("+++++++++++", newNotification)

        dispatch(addNotification(newNotification.notification))
      })
    } catch (error) {
      console.error("Error setting up socket connection:", error)
    }
  }

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("token")
      const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/readNotification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ notificationId }),
      })

      if (response.ok) {
        // Update Redux store to mark notification as read
        dispatch(updateNotification({ id: notificationId }))
      }
    } catch (error) {
      console.error("Error marking notification as read:", error)
    }
  }

  const handleNotificationClick = (notification) => {
    // Mark as read when clicked
    if (!notification.isRead) {
      markAsRead(notification._id)
    }

    // Navigate to relevant page if needed
    if (notification.link) {
      navigate(notification.link)
      setIsNotificationOpen(false)
    }
  }

  const toggleNotifications = () => {
    setIsNotificationOpen(!isNotificationOpen);
  }

  const handleNavClick = (item) => {
    if (item.path.includes("/leads")) {
      dispatch(setLeadsPage(1));
    }
    if (item.path.includes("/application-log")) {
      dispatch(setApplicationLogPage(1));
    }
  }

  const getNavTarget = (item) => {
    if (item.path.includes("/application-log")) {
      return { pathname: item.path, search: "?page=1" }
    }
    return { pathname: item.path, search: "" }
  }

  // const handleMarkAllAsRead = async () => {
  //   try {
  //     const token = localStorage.getItem("token")
  //     const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/readAllNotifications`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${token}`,
  //       },
  //     })

  //     if (response.ok) {
  //       dispatch(markAllNotificationsAsRead())
  //     }
  //   } catch (error) {
  //     console.error("Error marking all notifications as read:", error)
  //   }
  // }

  const handleLogout = () => {
    localStorage.removeItem("token")
    localStorage.removeItem("user")

    dispatch(clearNotifications())
    dispatch(clearLeads())
    dispatch(logout())

    socketService.disconnect()

    navigate("/")
  }

  const handleLogoClick = () => {
    navigate("/agent")
  }

  const handleCheckboxChange = (countryName) => {
    // Calculate new selection first
    const currentSelection = selectedCountries;
    const updatedSelection = currentSelection.includes(countryName)
      ? currentSelection.filter((name) => name !== countryName)
      : [...currentSelection, countryName];
    
    // Update local state
    setSelectedCountries(updatedSelection);
    setIsOpen(false);
    
    // Dispatch to global store separately to avoid dispatch-during-render warning
    dispatch(setGlobalCountries(updatedSelection));
  }

  return (
    <header className="bg-[#005787] text-white px-4 xl:px-6 py-4 lg:py-6 relative">
      <div className="flex items-center justify-between gap-2">
        {/* Logo */}
        <div className="flex-shrink-0">
          <button
            type="button"
            onClick={handleLogoClick}
            className="cursor-pointer"
            aria-label="Go to dashboard"
          >
            <img
              loading={"lazy"}
              src={logo || "/placeholder.svg"}
              alt="World Class Logo"
              width={110}
              height={40}
              className="xl:w-[120px]"
            />
          </button>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex flex-1 min-w-0 justify-center">
          <ul className="flex flex-wrap justify-center items-center gap-x-2 xl:gap-x-4 gap-y-1">
            {filteredNavItems.map((item) => (
              <li key={item.name} className="flex-shrink-0">
                <NavLink
                  to={getNavTarget(item)}
                  end={item.path === "/agent"}
                  onClick={() => handleNavClick(item)}
                  className={({ isActive }) =>
                    `cursor-pointer transition-colors duration-200 relative pb-1 text-[13px] xl:text-sm whitespace-nowrap ${isActive
                      ? 'text-white after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-[40px] after:h-[3px] after:bg-[#87CA60] after:rounded-sm'
                      : ''
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Desktop Right Section */}
        <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-shrink-0">
          <div ref={countryDropdownRef} className="relative text-black flex-shrink-0">
            {!hideDropdown && (
              <div>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex items-center gap-1.5 rounded-full px-2 py-1.5 text-xs xl:text-sm border border-[#87CA60]/60 bg-white drop-shadow-md"
                  style={{ minWidth: '90px', maxWidth: '160px' }}
                >
                  {selectedCountries.length > 0 ? (
                    <div className="flex items-center gap-0.5 overflow-hidden max-w-[90px]">
                      {selectedCountries.slice(0, 8).map((name) => {
                        const country = countries.find((c) => c.name === name)
                        if (!country) return null; // Safe guard against undefined country
                        return (
                          <img
                            key={country?.name}
                            src={country?.flag || "/placeholder.svg"}
                            alt={country?.name}
                            className="w-5 h-5 rounded-full object-cover border border-slate-300 flex-shrink-0"
                          />
                        )
                      })}
                    </div>
                  ) : (
                    <span className="text-gray-500 text-xs whitespace-nowrap">Select country</span>
                  )}
                  <svg
                    className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="country-menu"
                      initial={{ opacity: 0, y: -6, scale: 0.98 }}
                      animate={{ opacity: 1, y: 4, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.98 }}
                      transition={{ duration: 0.18 }}
                      className="absolute left-0 top-full mt-1 w-[200px] bg-white border rounded-lg shadow-lg py-1 z-10"
                    >
                      {countries.map((country) => (
                        <label
                          key={country.name}
                          className="w-full flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 space-x-2"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCountries.includes(country.name)}
                            onChange={() => handleCheckboxChange(country.name)}
                            className="cursor-pointer"
                          />
                          <img
                            src={country.flag || "/placeholder.svg"}
                            alt={country.name}
                            loading="lazy"
                            className="w-5 h-5 rounded-full object-cover"
                          />
                          <span>{country.name}</span>
                        </label>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
          <div className="relative flex-shrink-0" data-notification-trigger="true">
            <FiBell className="cursor-pointer text-[#87CA60] h-5 w-5 xl:h-6 xl:w-6" strokeWidth={3} onClick={toggleNotifications} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="relative group text-slate-800 flex-shrink-0">
            <div className="flex items-center gap-1.5 bg-[#87CA60] px-2 py-1.5 rounded-full cursor-pointer">
              <MdOutlineFaceUnlock className="h-5 w-5 bg-white text-slate-700 rounded-full flex-shrink-0" />
              <span className="font-medium text-xs xl:text-sm max-w-[72px] truncate">Hello {user?.firstName ? user.firstName.split(" ")[0] : "Guest"}</span>
              <FaCaretDown className="text-white w-3.5 h-3.5 flex-shrink-0" />
            </div>

            {/* Dropdown */}
            <div className="absolute z-50 right-0 w-48 bg-transparent pt-2">
              <div className="bg-[#87CA60] shadow-lg rounded-lg hidden group-hover:block">
                <ul className="py-2">
                  <li
                    onClick={() => navigate('/agent/settings')}
                    className="px-4 py-2 hover:bg-gray-100/10 cursor-pointer text-sm"
                  >
                    Settings
                  </li>
                  <li
                    onClick={handleLogout}
                    className="px-4 py-2 hover:bg-gray-100/10 cursor-pointer text-sm"
                  >
                    Logout
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex-shrink-0 flex items-center gap-3">
          <div className="relative" data-notification-trigger="true">
            <button
              className="relative focus:outline-none"
              onClick={toggleNotifications}
              aria-label="Notifications"
            >
              <FiBell className="cursor-pointer text-[#87CA60] h-6 w-6" strokeWidth={3} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
          <MenuToggleIcon isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden"
          >
            <div className="mt-4 pb-4">
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="flex items-center space-x-2 mb-4"
              ></motion.div>
              <nav>
                <ul className="space-y-2">
                  {filteredNavItems.map((item, index) => (
                    <motion.li
                      key={item.name}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <NavLink
                        to={getNavTarget(item)}
                        end={item.path === "/agent"}
                        onClick={() => {
                          handleNavClick(item)
                          setIsMobileMenuOpen(false)
                        }}
                        className={({ isActive }) =>
                          `block hover:bg-white/10 px-2 py-1 rounded cursor-pointer relative ${isActive
                            ? "bg-white/10 text-white font-medium after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:bg-white"
                            : ""
                          }`
                        }
                      >
                        {item.name}
                      </NavLink>
                    </motion.li>
                  ))}
                  {/* {!hideDropdown && (
                    <motion.li
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 4 * 0.1 }}
                    >
                      <div className="block px-2 py-3">
                        <div className="text-sm mb-2">Select Countries:</div>
                        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto bg-white/10 p-2 rounded">
                          {countries.map((country) => (
                            <label key={country.name} className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={selectedCountries.includes(country.name)}
                                onChange={() => handleCheckboxChange(country.name)}
                                className="w-3 h-3"
                              />
                              <img
                                src={country.flag || "/placeholder.svg"}
                                alt={country.name}
                                className="w-4 h-4 rounded-full"
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    </motion.li>
                  )} */}
                  <motion.li
                    key={"Settings"}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 5 * 0.1 }}
                  >
                    <NavLink
                      to={"/agent/settings"}
                      end={"/agent/settings" === "/agent"}
                      className={({ isActive }) =>
                        `block hover:bg-white/10 px-2 py-1 rounded cursor-pointer relative ${isActive
                          ? "bg-white/10 text-white font-medium after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:bg-white"
                          : ""
                        }`
                      }
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {"Settings"}
                    </NavLink>
                  </motion.li>
                </ul>
              </nav>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex items-center justify-between mt-4 pt-4 border-t border-white/20"
              >
                <div className="flex items-center space-x-2">
                  <span>Hello {user?.firstName ? user.firstName.split(" ")[0] : "Guest"}</span>
                  <MdOutlineFaceUnlock className="h-6 w-6 bg-white text-slate-700 rounded-full" />
                </div>
                <button
                  onClick={handleLogout}
                  className="bg-white text-red-500 flex flex-row items-center gap-3 p-2 rounded-lg"
                >
                  <FiLogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification Panel */}
      <AnimatePresence>
        {isNotificationOpen && (
          <motion.div
            ref={notificationRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed top-[80px] right-4 md:right-6 lg:absolute lg:top-full lg:right-0 w-[90%] md:w-96 bg-[#FAF6F1] border border-gray-200 rounded-lg shadow-lg z-[1000] max-h-[80vh] flex flex-col"
            style={{ maxHeight: 'calc(100vh - 200px)' }}
          >
            <div className="p-4 border-b border-gray-200 bg-[#005787] text-white flex items-center justify-between">
              <h3 className="text-lg font-medium">Notifications</h3>
              <div className="flex items-center space-x-3">
                {/* {notifications && notifications.some((n) => !n.isRead) && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="flex items-center text-sm hover:text-[#87CA60] transition-colors"
                  >
                    <FiCheckCircle className="mr-1" />
                    <span className="hidden sm:inline">Mark all as read</span>
                  </button>
                )} */}
                <button
                  onClick={toggleNotifications}
                  className="p-1 rounded-full hover:bg-[#87CA60] transition-colors"
                  aria-label="Close notifications"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {notifications && notifications.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <div
                      key={notification._id}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${!notification.isRead ? "bg-blue-50" : ""
                        }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start">
                        {!notification.isRead && (
                          <span className="h-2 w-2 mt-1.5 rounded-full bg-blue-500 mr-2 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className="text-sm text-gray-800">{notification.message}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(notification.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">No notifications</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
